import { useState } from "react";
import { X, Upload } from "lucide-react";
import Txt from "../../Text";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import CustomSelect from "../../CustomSelect";
import DateInput from "../../form/DateInput";
import api from "../../../api";
import { apiRequest, apiSuccess } from "../../../redux/api/apiActions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { showAlert } from "../../../redux/alert/alertActions";
import errorChecker from "../../../utils/errorChecker";

const Text = ({ children, className, page = "TOOLS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

export default function AddRecordModal({ darkMode, defaultLanguage, theme, onClose }) {
  const createMaterialKey = "createMaterial";
  const { requests } = useSelector((state) => state.api);
  const loading = requests[createMaterialKey]?.loading;
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: defaultLanguage === "ar" ? "" : "",
    category: "Wound Care",
    location: "",
    serialNo: "",
    quantity: "",
    inUse: "",
    status: "In Stock",
    condition: 80,
    image: "https://via.placeholder.com/300",
    lastAudit: new Date().toISOString().split('T')[0],
    nextAudit: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const categoryOptions = {
    en: [
      { value: "Wound Care", label: "Wound Care" },
      { value: "PPE", label: "PPE" },
      { value: "IV Supplies", label: "IV Supplies" },
      { value: "Medications", label: "Medications" },
      { value: "Catheters", label: "Catheters" },
      { value: "Injection Supplies", label: "Injection Supplies" },
      { value: "Disinfectants", label: "Disinfectants" },
      { value: "Supplies", label: "Supplies" },
      { value: "Surgical Supplies", label: "Surgical Supplies" }
    ],
    fr: [
      { value: "Wound Care", label: "Soins des plaies" },
      { value: "PPE", label: "EPI" },
      { value: "IV Supplies", label: "Fournitures IV" },
      { value: "Medications", label: "Médicaments" },
      { value: "Catheters", label: "Cathéters" },
      { value: "Injection Supplies", label: "Fournitures d'injection" },
      { value: "Disinfectants", label: "Désinfectants" },
      { value: "Supplies", label: "Fournitures" },
      { value: "Surgical Supplies", label: "Fournitures chirurgicales" }  
    ],
    ar: [
      { value: "Wound Care", label: "العناية بالجروح" },
      { value: "PPE", label: "معدات الحماية الشخصية" },
      { value: "IV Supplies", label: "مستلزمات الوريد" },
      { value: "Medications", label: "الأدوية" },
      { value: "Catheters", label: "القساطر" },
      { value: "Injection Supplies", label: "مستلزمات الحقن" },
      { value: "Disinfectants", label: "المطهرات" },
      { value: "Supplies", label: "المستلزمات" },
      { value: "Surgical Supplies", label: "المستلزمات الجراحية" }
    ] 
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === "quantity" || name === "inUse" || name === "condition" ? parseInt(value) : value
    }));
  };

 

  const [imageFile, setImageFile] = useState(null);


const handleImageChange = (e) => {
  const file = e.target.files?.[0];
  if (file) {
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        image: reader.result
      }));
    };
    reader.readAsDataURL(file);
  }
};


const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    dispatch(apiRequest("POST", createMaterialKey));

    // Create FormData for multipart request (needed for file upload)
    const submitData = new FormData();
    
    // Add form fields to FormData
    submitData.append("name", formData.name);
    submitData.append("category", formData.category);
    submitData.append("location", formData.location);
    submitData.append("serialNo", formData.serialNo);
    submitData.append("quantity", formData.quantity || 0);
    submitData.append("inUse", formData.inUse || 0);
    submitData.append("status", formData.status);
    submitData.append("condition", formData.condition);
    submitData.append("lastAudit", formData.lastAudit);
    submitData.append("nextAudit", formData.nextAudit);

    // Add image file if user selected one
    if (imageFile) {
     
      submitData.append("file", imageFile);
    }

    // Make API request to create material
    const response = await api.post("materials/create", submitData);

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(response.data?.details || response.data?.message || "Failed to create material");
    }
dispatch(
          showAlert({
            type: "success",
            title: textTranslator("Success", "PROFILE"),
            message: textTranslator("Material created successfully", "TOOLS"),
          }),
        );
    dispatch(apiSuccess("POST", createMaterialKey, "Material created successfully"));
    
            
    
    // Reset form
    setFormData({
      name: "",
      category: "Wound Care",
      location: "",
      serialNo: "",
      quantity: "",
      inUse: "",
      status: "In Stock",
      condition: 80,
      image: "https://via.placeholder.com/300",
      lastAudit: new Date().toISOString().split('T')[0],
      nextAudit: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
    setImageFile(null);
    
    // Close modal
    onClose();
  } catch (error) {
    const message = error.response?.data?.details || error.response?.data?.message || "An error occurred while creating material";
    const type = error.response?.data?.type || 404;
    const title = textTranslator("Post Error", "PROFILE");
    errorChecker(
          message,
          type,
          dispatch,
          title
        );
  } 
};

  return (
    <div className={arClass(`fixed inset-0 z-[1000] flex items-center justify-center p-4`)}>
      <div className={`absolute inset-0 ${theme.modalOverlay} backdrop-blur-sm`} onClick={onClose} />
      <div
        className={arClass(`relative ${theme.cardBg} border ${theme.border} rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto`)}
        onClick={(e) => e.stopPropagation()}
      >
<div className={arClass(`sticky top-0 px-6 py-4 border-b z-[9999] ${darkMode ? "bg-slate-800" : "bg-slate-100"} ${theme.border} flex justify-between items-center back`)}>          <h2 className={`text-2xl font-bold ${theme.text}`}><Text>Add New Material</Text></h2>
          <button
            onClick={onClose}
            className={arClass(`w-8 h-8 ${darkMode ? "hover:bg-slate-700" : "hover:bg-slate-200"} rounded-full flex items-center justify-center transition-colors`)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 normal-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Image Upload */}
            <div className="md:col-span-2">
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Material Image</Text></label>
              <div className={arClass(`relative flex flex-col items-center justify-center w-full border-2 border-dashed ${theme.inputBorder} rounded-xl p-6 cursor-pointer hover:opacity-80 transition-opacity`)}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                {formData.image && formData.image !== "https://via.placeholder.com/300" ? (
                  <div className="flex flex-col items-center gap-3">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                    <p className={`text-sm ${theme.text}`}><Text>Click to change image</Text></p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className={`w-8 h-8 ${theme.text}`} />
                    <p className={`text-sm font-medium ${theme.text}`}><Text>Click to upload image</Text></p>
                    <p className={`text-xs ${theme.text} opacity-70`}>(PNG, JPG, GIF, WebP)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Name Field */}
            <div className="md:col-span-2">
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Material Name</Text></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={textTranslator("Enter material name", "TOOLS")}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Category</Text></label>
              <CustomSelect
                options={categoryOptions[defaultLanguage]}
                value={formData.category}
                onChange={(val) => setFormData(prev => ({ ...prev, category: val.target.value }))}
                allowSearch={true}
                allowCreate={true}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
              />
            </div>

            {/* Location */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Location</Text></label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder={textTranslator("e.g., Room 101, Shelf A", "TOOLS")}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
                required
              />
            </div>

            {/* Serial Number */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Serial Number</Text></label>
              <input
                type="text"
                name="serialNo"
                value={formData.serialNo}
                onChange={handleChange}
                placeholder={textTranslator("Enter serial number", "TOOLS")}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
                required
              />
            </div>

            {/* Total Quantity */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Total Quantity</Text></label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="0"
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
                required
              />
            </div>

            {/* In Use */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Currently In Use</Text></label>
              <input
                type="number"
                name="inUse"
                value={formData.inUse}
                onChange={handleChange}
                placeholder="0"
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
                required
              />
            </div>

            {/* Status */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Status</Text></label>
              <CustomSelect
                options={[
                  { value: "In Stock", label: textTranslator("In Stock", "TOOLS") },
                  { value: "Low Stock", label: textTranslator("Low Stock", "TOOLS") },
                  { value: "Out of Stock", label: textTranslator("Out of Stock", "TOOLS") },
                ]}
                value={formData.status}
                onChange={(val) => setFormData(prev => ({ ...prev, status: val.target.value }))}
                allowSearch={false}
                allowCreate={false}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
              />
            </div>

            {/* Last Audit Date */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Last Audit Date</Text></label>
              <DateInput
                name="lastAudit"
                value={formData.lastAudit}
                onChange={handleChange}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
              />
            </div>

            {/* Next Audit Date */}
            <div>
              <label className={`block text-sm font-semibold ${theme.text} mb-2`}><Text>Next Audit Date</Text></label>
              <DateInput
                name="nextAudit"
                value={formData.nextAudit}
                onChange={handleChange}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
              />
            </div>

            {/* Condition Score */}
            <div className={arClass("md:col-span-2")}>
              <label className={arClass(`block text-sm font-semibold ${theme.text} mb-2`)}><Text>Stock Level Score</Text>({formData.condition}%)</label>
              <input
                type="range"
                name="condition"
                min="0"
                max="100"
                value={formData.condition}
                onChange={handleChange}
                className={arClass(`w-full ${theme.inputBg} border ${theme.inputBorder} rounded-xl px-4 py-3 ${theme.text} focus:outline-none focus:border-cyan-500 transition-colors`)}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <button
            disabled={loading}
              type="button"
              onClick={onClose}
              className={`flex-1 disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? "bg-slate-800 hover:bg-slate-700 text-slate-300" : "bg-slate-200 hover:bg-slate-300 text-slate-700"} font-semibold py-3 rounded-xl text-sm transition-colors`}
            >
              <Text>Cancel</Text>
            </button>
            <button
              disabled={loading}
              type="submit"
              className={`flex-1 bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed  ${darkMode ? "hover:bg-emerald-400 text-slate-950 " : "hover:bg-emerald-600 text-white"} font-bold py-3 rounded-xl text-sm transition-colors`}
            >
              <Text>{loading ? "Adding..." : "Add Material"}</Text>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
